import { useEffect, useState } from "react";
import { Table, type TableColumnsType } from "antd";
import { IoIosArrowDown } from "react-icons/io";
import { useTabsContext } from "../../../../contexts/tabsContext";
import { useNavigate } from "react-router-dom";
import "./statsList.css";
import SourceTag from "../../../masterComponents/SourceTag";
import { useFiltersContext } from "../../../../contexts/filtersContext";

const StatsList = ({ statisticsData }) => {
  const { setActiveTabKey } = useTabsContext();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const { updateFilter } = useFiltersContext();

  const navigate = useNavigate();

  useEffect(() => {
    if (!statisticsData?.length) {
      setExpandedRowKeys([]);
      return;
    }
    const keys = statisticsData
      .filter((item) => item.characteristics?.length)
      .map((item) => item.key);
    setExpandedRowKeys(keys);
  }, [statisticsData]);

  const handleMetricSelect = (attrCode, countName, className) => {
    updateFilter("source", countName);
    updateFilter("class_name", [className]);
    setActiveTabKey("AnalysisAgent");
    navigate(`/?attr=${attrCode}`, { replace: true });
  };

  const columns: TableColumnsType = [
    {
      title: "Classification",
      dataIndex: "class_name",
      key: "class_name",
      align: "center",
      render: (_, record) => (
        <span
          style={{
            fontWeight: "bold",
            color: "#303846",
            letterSpacing: "2px",
            padding: "0px var(--spacing-sm)",
          }}
        >
          {record.class_name ? `${record.class_name}` : null}
        </span>
      ),
    },
    {
      title: "Characteristic",
      dataIndex: "characteristic_name",
      key: "characteristic_name",
      align: "left",
      render: (_, record) => (
        <>
          {record.characteristic_name
            ? `${record.characteristic_name.attr_code} (${record.characteristic_name.attr_descr})`
            : null}
        </>
      ),
    },
    {
      title: "Unverified",
      dataIndex: "unverified_count",
      key: "unverified_count",
      align: "start",
      render: (_, record) =>
        record.unverified_count && (
          <>
            <SourceTag
              source="llm"
              text={record.unverified_count?.value}
              style={{
                padding: "0px var(--spacing-sm)",
                fontSize: 14,
                fontWeight: 600,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleMetricSelect(
                  record.characteristic_name.attr_code,
                  ["llm"],
                  record.className,
                );
              }}
            />
            <span
              style={{
                margin: "var(--spacing-xs)",
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              {record.percentageUnverified}
            </span>
          </>
        ),
    },
    {
      title: "Verified",
      dataIndex: "verified_count",
      key: "verified_count",
      align: "start",
      render: (_, record) =>
        record.verified_count && (
          <>
            <SourceTag
              source="finals"
              text={record.verified_count?.value}
              style={{
                padding: "0px var(--spacing-sm)",
                fontSize: 14,
                fontWeight: 600,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleMetricSelect(
                  record.characteristic_name.attr_code,
                  ["finals"],
                  record.className,
                );
              }}
            />
            <span
              style={{
                margin: "var(--spacing-xs)",
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              {record.percentageVerified}
            </span>
          </>
        ),
    },
    {
      title: "Validated",
      dataIndex: "validation_count",
      key: "validation_count",
      align: "start",
      render: (_, record) =>
        record.validation_count && (
          <>
            <SourceTag
              source="validated"
              text={record.validation_count?.value}
              style={{
                padding: "0px var(--spacing-sm)",
                fontSize: 14,
                fontWeight: 600,
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleMetricSelect(
                  record.characteristic_name.attr_code,
                  ["validated"],
                  record.className,
                );
              }}
            />
            <span
              style={{
                margin: "var(--spacing-xs)",
                color: "rgba(0, 0, 0, 0.65)",
              }}
            >
              {record.percentageValidated}
            </span>
          </>
        ),
    },
    {
      title: "Total",
      dataIndex: "total_count",
      key: "total_count",
      align: "center",
      render: (_, record) =>
        record.total_count?.value && (
          <span
            style={{ fontWeight: "bold", color: "#303846" }}
            onClick={(e) => {
              e.stopPropagation();
              handleMetricSelect(
                record.characteristic_name.attr_code,
                ["llm", "finals", "validated"],
                record.className,
              );
            }}
          >
            {record.total_count?.value}
          </span>
        ),
    },
  ];

  return (
    <Table
      rowKey="key"
      columns={columns}
      className="stats-list"
      tableLayout="auto"
      dataSource={statisticsData}
      pagination={false}
      expandable={{
        childrenColumnName: "characteristics",
        expandRowByClick: true,
        expandedRowKeys,
        onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
        expandIcon: ({ expanded, onExpand, record }) => {
          if (record.characteristics?.length) {
            return (
              <IoIosArrowDown
                onClick={(e) => {
                  e.stopPropagation();
                  onExpand(record, e);
                }}
                style={{
                  transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.4s ease",
                  cursor: "pointer",
                }}
              />
            );
          }
        },
      }}
    />
  );
};
export default StatsList;
